import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Chip, IconButton, Box, Typography, Pagination } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { DataGrid, GridToolbarColumnsButton } from "@mui/x-data-grid";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SyncIcon from "@mui/icons-material/Sync";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";

import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import CustomSnackbar from "../Common/snackBar";
import { DataGrid_SX } from "../../Assets/customTheme/constants";
import { CustomNoRowsOverlay } from "../../Assets/customTheme/CustomNoRowsOverlay";
import { default as CustomTooltip, default as CoustomTooltip } from "../../Assets/customTheme/customtooltip";
import { TableColumns, TableFilte, TableReset } from "../../Assets/svg/svgicon";
import { MyAddRoundedIcon, MyDeleteOutlineOutlinedIcon, MyEditOutlinedIcon, MySearchIcon } from "../../Assets/svg/MyIcons";
import { CommonTooltip } from "../../utils/tooltip";
import showConfirmation from "../../utils/Conformation";
import { getPermissionData, getUserData } from "../../Actions/common";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import { formatDateTime } from "../Common/DateFormat";
import { getModuleArticlesAction, deleteTutorialAction, getModulesAction } from "../../Actions/KnowledgeBaseAction";
import KnowledgeBaseFormDialog from "./KnowledgeBaseFormDialog";
import TutorialPlayerModal from "./TutorialPlayerModal";

const UploadStatusBadge = ({ status }) => {
  const cfg = {
    pending: { label: "Queued", color: "warning", icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} /> },
    processing: {
      label: "Processing",
      color: "info",
      icon: (
        <SyncIcon
          sx={{
            fontSize: 14,
            animation: "kb-spin 1.2s linear infinite",
            "@keyframes kb-spin": {
              from: { transform: "rotate(0deg)" },
              to: { transform: "rotate(360deg)" },
            },
          }}
        />
      ),
    },
    completed: { label: "Ready", color: "success", icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> },
    failed: { label: "Failed", color: "error", icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} /> },
  }[status] || { label: status || "—", color: "default" };

  return (
    <Chip
      icon={cfg.icon}
      label={cfg.label}
      color={cfg.color}
      size="small"
      sx={{ fontWeight: 600, fontSize: "0.7rem", height: 24 }}
    />
  );
};

const countAttachmentsByType = (attachments) => {
  const list = Array.isArray(attachments) ? attachments : [];
  const videos = list.filter((a) => a.mediaType === "video").length;
  const images = list.filter((a) => a.mediaType === "image").length;
  const docs = list.filter((a) => a.mediaType === "document" || a.mediaType === "raw").length;
  return { videos, images, docs };
};

const CustomToolbar = ({
  searchValue,
  handleSearchApi,
  handleFilterData,
  handleResetFilters,
  hasPermission,
  filterButtonDisabled,
  resetButtonDisabled,
  handleAddTutorial,
}) => (
  <div className="custom-header">
    <div className="card border-rounded-0 fiter-card">
      <div className="card-body p-5 pb-0">
        <div className="row">
          <div className="col-xl-4 col-lg-5 col-md-6 col-sm-12 mb-5">
            <div className="input-group w-100">
              <div className="input-group-prepend">
                <span className="input-group-text" id="kb-search-addon">
                  <MySearchIcon />
                </span>
              </div>
              <input
                id="kb-search-input"
                type="text"
                className="form-control"
                placeholder="Search module knowledge base..."
                name="search"
                value={searchValue}
                onChange={handleSearchApi}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !filterButtonDisabled) {
                    handleFilterData();
                  }
                }}
              />
            </div>
          </div>

          <div className="col-auto ms-auto d-flex gap-3 mb-5 align-items-center">
            <CustomTooltip title="Filter" placement="top" arrow>
              <span>
                <button
                  className="btn text-primary btn-light btn-icon"
                  onClick={handleFilterData}
                  disabled={filterButtonDisabled}
                >
                  <TableFilte />
                </button>
              </span>
            </CustomTooltip>

            <CustomTooltip title="Reset" placement="top" arrow>
              <span>
                <button
                  className="btn text-primary btn-light btn-icon"
                  onClick={handleResetFilters}
                  disabled={resetButtonDisabled}
                >
                  <TableReset />
                </button>
              </span>
            </CustomTooltip>

            <CoustomTooltip title="Column" placement="top" arrow>
              <GridToolbarColumnsButton
                slotProps={{
                  button: {
                    className: "btn btn-light text-primary btn-icon grid-toolbar d-flex justify-content-center align-items-center p-0",
                    style: { width: "47px", height: "44px", minWidth: "unset", borderRadius: "8px" },
                    startIcon: <TableColumns />,
                  },
                }}
              />
            </CoustomTooltip>

            {hasPermission?.canCreate && (
              <button
                className="btn btn-outline btn-primary btn-add"
                onClick={handleAddTutorial}
              >
                <MyAddRoundedIcon className="me-1 text-white" /> ADD
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ModuleListPage = () => {
  const { moduleSlug } = useParams();
  const navigate = useNavigate();
  const isAllView = moduleSlug === "all" || moduleSlug === "ALL";

  const userData = localStorage.getItem("userData") ? getUserData() : null;
  const permissionData = localStorage.getItem(`${userData?.id}_permissionData`)
    ? getPermissionData(`${userData?.id}_permissionData`)
    : [];
  const hasPermission = permissionData[0]?.role_permissions?.find(
    (m) => m?.module && m?.module?.slug === "knowledge-base"
  );

  const [moduleInfo, setModuleInfo] = useState(null);
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);

  // Pagination — matches Plan Management pattern
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowsPerPage, setSelectedRowsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
  const [shouldFetchData, setShouldFetchData] = useState(1);

  const [searchValue, setSearchValue] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [filterButtonDisabled, setFilterButtonDisabled] = useState(true);
  const [resetButtonDisabled, setResetButtonDisabled] = useState(true);

  const [formDialog, setFormDialog] = useState({ open: false, tutorial: null });
  const [playerModal, setPlayerModal] = useState({ open: false, tutorial: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [allModules, setAllModules] = useState([]);

  useEffect(() => {
    getModulesAction().then(res => setAllModules(Array.isArray(res) ? res : []));
  }, []);

  const showSnack = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getModuleArticlesAction(moduleSlug, {
        search: activeSearch,
        page: currentPage,
        limit: selectedRowsPerPage,
      });
      setTutorials(Array.isArray(res.articles) ? res.articles : []);
      setTotalRecords(res.total || 0);
      setModuleInfo(res.module || null);
    } catch (err) {
      showSnack("Failed to load articles", "error");
    } finally {
      setLoading(false);
    }
  }, [moduleSlug, activeSearch, currentPage, selectedRowsPerPage]);

  useEffect(() => {
    if (shouldFetchData) {
      loadData();
      setShouldFetchData(0);
    }
    // eslint-disable-next-line
  }, [shouldFetchData, loadData]);

  // Re-fetch when moduleSlug changes
  useEffect(() => {
    setCurrentPage(1);
    setShouldFetchData(1);
    // eslint-disable-next-line
  }, [moduleSlug]);

  const handleDeleteTutorial = async (e, id) => {
    e.stopPropagation();
    await showConfirmation("simpleDelete", {
      entityName: "Tutorial",
      entityId: id,
      customText: "You will not be able to recover this tutorial!",
      action: async (tutorialId) => await deleteTutorialAction(tutorialId),
      onSuccess: () => {
        setShouldFetchData(1);
        showSnack("Tutorial deleted successfully.");
      },
      onError: (error) => {
        showSnack(error?.response?.data?.message || "Failed to delete tutorial.", "error");
      },
      showCancelledMsg: true,
    });
  };

  const handleSearchApi = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    setFilterButtonDisabled(val.trim() === activeSearch);
  };

  const handleFilterData = () => {
    setTutorials([]);
    setActiveSearch(searchValue.trim());
    setCurrentPage(1);
    setShouldFetchData(1);
    setFilterButtonDisabled(true);
    setResetButtonDisabled(searchValue.trim() === "");
  };

  const handleResetFilters = () => {
    setTutorials([]);
    setSearchValue("");
    setActiveSearch("");
    setCurrentPage(1);
    setShouldFetchData(1);
    setFilterButtonDisabled(true);
    setResetButtonDisabled(true);
  };

  const handlePaginationChange = (event, value) => {
    setTutorials([]);
    setCurrentPage(value);
    setShouldFetchData(1);
  };

  const handleRowsPerPageChange = (event) => {
    setTutorials([]);
    const newValue = parseInt(event.target.value);
    setSelectedRowsPerPage(newValue);
    setCurrentPage(1);
    setLoading(true);
    setShouldFetchData(1);
  };

  const columns = [
    {
      field: "title",
      headerName: "Title",
      minWidth: 380,
      flex: 2,
      renderCell: ({ row }) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "4px 8px",
            cursor: "pointer",
            width: "100%",
            height: "100%",
          }}
          onClick={() => navigate(`/knowledge-base/${row.moduleSlug || moduleSlug}/${row.id}`)}
        >
          <div style={{ minWidth: 0, width: "100%" }}>
            <span
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: "#333",
                display: "block",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {row.title || "N/A"}
            </span>
            {row.shortDescription && (
              <span
                style={{
                  fontSize: 12,
                  color: "#888",
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {row.shortDescription}
              </span>
            )}
          </div>
        </div>
      ),
    },
    ...(isAllView
      ? [
        {
          field: "moduleName",
          headerName: "Module",
          minWidth: 180,
          flex: 1.4,
          renderCell: ({ value }) => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                height: "100%",
                px: 1.5,
              }}
            >
              <Chip
                label={value || "—"}
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: "rgba(34, 65, 158, 0.08)",
                  color: "#22419e",
                  border: "1px solid rgba(34, 65, 158, 0.18)",
                }}
              />
            </Box>
          ),
        },
      ]
      : []),
    {
      field: "uploadStatus",
      headerName: "Status",
      minWidth: 120,
      flex: 0.8,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <UploadStatusBadge status={value} />
        </Box>
      ),
    },
    {
      field: "attachments",
      headerName: "Attachments",
      minWidth: 170,
      flex: 1.2,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: ({ row }) => {
        const { videos, images, docs } = countAttachmentsByType(row.attachments);
        const total = videos + images + docs;
        if (total === 0) {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <Typography sx={{ fontSize: 18, color: "text.secondary", fontWeight: 500 }}>
                {"—"}
              </Typography>
            </Box>
          );
        }
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.25,
              width: "100%",
              height: "100%",
            }}
          >
            {videos > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <VideocamOutlinedIcon sx={{ fontSize: 18, color: "#0ea5e9" }} />
                <Typography sx={{ fontSize: 14, color: "#0ea5e9", fontWeight: 600 }}>
                  {videos}
                </Typography>
              </Box>
            )}
            {images > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <ImageOutlinedIcon sx={{ fontSize: 18, color: "#6366f1" }} />
                <Typography sx={{ fontSize: 14, color: "#6366f1", fontWeight: 600 }}>
                  {images}
                </Typography>
              </Box>
            )}
            {docs > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <DescriptionOutlinedIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                <Typography sx={{ fontSize: 14, color: "#16a34a", fontWeight: 600 }}>
                  {docs}
                </Typography>
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      field: "engagement",
      headerName: "Engagement",
      minWidth: 150,
      flex: 1,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            width: "100%",
            height: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <ThumbUpAltOutlinedIcon sx={{ fontSize: 16, color: "#22c55e" }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#22c55e" }}>
              {row.totalHelpful || 0}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <ThumbDownAltOutlinedIcon sx={{ fontSize: 16, color: "#ef4444" }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#ef4444" }}>
              {row.totalUnhelpful || 0}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      minWidth: 160,
      flex: 1.2,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 400, color: "#555" }}>
            {formatDateTime(value, true)}
          </Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Action",
      sortable: false,
      minWidth: 170,
      flex: 1,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => (
        <div
          className="d-flex align-items-center w-100 h-100"
          style={{ justifyContent: "flex-end", paddingRight: 8 }}
        >
          <CommonTooltip title="View" placement="top" arrow>
            <IconButton
              className="btn-custom-i text-primary me-2"
              onClick={() => navigate(`/knowledge-base/${row.moduleSlug || moduleSlug}/${row.id}`)}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </CommonTooltip>
          {hasPermission?.canEdit && (
            <CommonTooltip title="Edit" placement="top" arrow>
              <IconButton
                className="btn-custom-i text-primary me-2"
                onClick={() => setFormDialog({ open: true, tutorial: row })}
              >
                <MyEditOutlinedIcon />
              </IconButton>
            </CommonTooltip>
          )}
          {hasPermission?.canDelete && (
            <CommonTooltip title="Delete" placement="top" arrow>
              <IconButton
                className="btn-custom-i text-primary"
                onClick={(e) => handleDeleteTutorial(e, row.id)}
              >
                <MyDeleteOutlineOutlinedIcon />
              </IconButton>
            </CommonTooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <div id="kt_content_container" style={{ paddingBottom: '60px' }}>
      <BreadcrumbsComponent />

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        {!isAllView && (
          <IconButton onClick={() => navigate("/knowledge-base")} sx={{ bgcolor: 'white', boxShadow: 1 }}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {isAllView
              ? "All Knowledge Base"
              : moduleInfo
                ? moduleInfo.name
                : "Module Knowledge Base"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isAllView
              ? `${totalRecords || tutorials.length} Knowledge Base entries across all modules`
              : `${moduleInfo?.articleCount || tutorials.length} Knowledge Base`}
          </Typography>
        </Box>
      </Box>

      <DataGrid
        className="border-0"
        localeText={{ toolbarColumns: "" }}
        rows={tutorials}
        columns={columns}
        autoHeight
        hideFooterRowCount={true}
        hideFooterPagination={true}
        hideFooter={true}
        loading={loading}
        disableRowSelectionOnClick
        getRowHeight={() => "auto"}
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        slotProps={{
          toolbar: {
            searchValue,
            handleSearchApi,
            handleFilterData,
            handleResetFilters,
            hasPermission,
            filterButtonDisabled,
            resetButtonDisabled,
            handleAddTutorial: () => setFormDialog({ open: true, tutorial: null }),
          },
        }}
        sx={{
          ...DataGrid_SX,
          "& .MuiDataGrid-cell": { py: 1, alignItems: "center" },
        }}
      />

      {/* Pagination Footer — matches Plan Management exactly */}
      <div className="card-footer d-flex align-items-center justify-content-between mt-7">
        <div className="d-flex align-items-center">
          <label className="fw-semibold me-3">Row per page </label>
          <FormControl variant="standard" style={{ minWidth: 50 }}>
            <Select
              labelId="kb-rows-per-page-label"
              id="kb-rows-per-page"
              value={selectedRowsPerPage}
              onChange={handleRowsPerPageChange}
              label="Row per page"
            >
              {ITEMS_PER_PAGE_OPTIONS?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <Pagination
          count={Math.ceil(totalRecords / selectedRowsPerPage) || 1}
          siblingCount={0}
          page={currentPage}
          onChange={(event, value) =>
            value !== currentPage ? handlePaginationChange(event, value) : null
          }
          variant="outlined"
          shape="rounded"
          className="custom-pagination mt-0 ms-5"
        />
      </div>

      <KnowledgeBaseFormDialog
        open={formDialog.open}
        onClose={() => setFormDialog({ open: false, tutorial: null })}
        tutorial={formDialog.tutorial}
        modules={allModules}
        fixedModuleSlug={moduleSlug}
        onSaved={() => {
          setShouldFetchData(1);
          setFormDialog({ open: false, tutorial: null });
          showSnack(formDialog.tutorial ? "Knowledge Base updated" : "Knowledge Base created", "success");
        }}
      />

      <TutorialPlayerModal
        open={playerModal.open}
        onClose={() => setPlayerModal({ open: false, tutorial: null })}
        tutorial={playerModal.tutorial}
      />

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      />
    </div>
  );
};

export default ModuleListPage;
